import React, { useMemo, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { MoonIcon } from '@heroicons/react/20/solid';
import BlockChip from './BlockChip';
import { DayHeader } from './timeGrid';
import { byTime, doctorName, hospitalLabel, initials, isWeekend, todayISO } from './scheduleUtils';

const COLS = '210px repeat(7, minmax(122px, 1fr))';
const surname = (d) => doctorName(d).split(' ').slice(-1)[0];

/**
 * Roster board — doctors down the side, days across, like a staff-rostering
 * app. The bottom Coverage row answers the practice question: which hospitals
 * are covered, who's on call, who's away and who is free to help.
 * Click a day heading to open that day; click a doctor to focus on them.
 */
const RosterView = ({ days, doctors, blocks, hospitalsById, colours, conflicts, canEdit, me, onAdd, onEdit, onMove, onOpenDay, onFocusDoctor }) => {
  const today = todayISO();
  const dragRef = useRef(null);
  const [dropKey, setDropKey] = useState(null);

  const cells = useMemo(() => {
    const m = {};
    blocks.forEach((b) => {
      const k = `${b.buid}|${b.bdate}`;
      (m[k] = m[k] || []).push(b);
    });
    Object.values(m).forEach((list) => list.sort(byTime));
    return m;
  }, [blocks]);

  // You may drop a session you can edit onto a row you can edit.
  const canDropOn = (buid) => !!dragRef.current && canEdit(dragRef.current.buid) && canEdit(buid);

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="grid min-w-[1064px]" style={{ gridTemplateColumns: COLS }}>
        {/* Header */}
        <div className="sticky left-0 top-0 z-30 flex items-end border-b border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Doctor
        </div>
        {days.map((day) => (
          <DayHeader key={day} day={day} today={today} onOpen={onOpenDay} className="sticky top-0 z-20 border-b border-l border-slate-200" />
        ))}

        {/* One row per doctor */}
        {doctors.map((d) => {
          const c = colours[d.uid];
          const mine = blocks.filter((b) => b.buid === d.uid);
          const lists = mine.filter((b) => b.btype === 'theatre').length;
          const calls = mine.filter((b) => b.btype === 'on_call').length;
          const editable = canEdit(d.uid);

          return (
            <React.Fragment key={d.uid}>
              <button
                type="button"
                onClick={() => onFocusDoctor(d.uid)}
                title="Show only this doctor"
                className="sticky left-0 z-10 flex items-center gap-2.5 border-t border-slate-100 bg-white px-3 py-2.5 text-left transition-colors hover:bg-teal-50"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white" style={{ background: c }}>
                  {initials(d)}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-800">
                    {doctorName(d)}
                    {d.uid === me && <span className="font-normal text-slate-400"> · you</span>}
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    {lists} list{lists === 1 ? '' : 's'}
                    {calls > 0 && ` · ${calls} on call`}
                  </span>
                </span>
              </button>

              {days.map((day) => {
                const key = `${d.uid}|${day}`;
                const items = cells[key] || [];
                const bg = dropKey === key
                  ? 'bg-brand-teal/10 ring-2 ring-inset ring-brand-teal'
                  : day === today ? 'bg-brand-teal/[0.04]' : isWeekend(day) ? 'bg-slate-50/70' : '';
                return (
                  <div
                    key={key}
                    className={`group relative flex min-h-[92px] flex-col gap-1 border-l border-t border-slate-100 p-1.5 ${bg} ${editable ? 'cursor-pointer' : ''}`}
                    onClick={(e) => { if (editable && e.target === e.currentTarget) onAdd({ buid: d.uid, bdate: day }); }}
                    onDragOver={(e) => {
                      if (!canDropOn(d.uid)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = e.ctrlKey || e.altKey ? 'copy' : 'move';
                      if (dropKey !== key) setDropKey(key);
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setDropKey((k) => (k === key ? null : k));
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const block = dragRef.current;
                      const allowed = canDropOn(d.uid);
                      dragRef.current = null;
                      setDropKey(null);
                      if (block && allowed) onMove(block, { buid: d.uid, bdate: day }, e.ctrlKey || e.altKey);
                    }}
                  >
                    {items.map((b) => (
                      <BlockChip
                        key={b.bid}
                        block={b}
                        hospital={hospitalsById[b.bhid]}
                        colour={colours[b.buid]}
                        conflict={conflicts.get(b.bid)}
                        draggable={canEdit(b.buid)}
                        onClick={() => onEdit(b)}
                        onDragStart={(e) => {
                          dragRef.current = b;
                          e.dataTransfer.effectAllowed = 'copyMove';
                          e.dataTransfer.setData('text/plain', String(b.bid));
                        }}
                        onDragEnd={() => { dragRef.current = null; setDropKey(null); }}
                      />
                    ))}
                    {editable && (
                      <button
                        type="button"
                        onClick={() => onAdd({ buid: d.uid, bdate: day })}
                        className="mt-auto flex items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 py-1 text-[11px] font-semibold text-slate-400 opacity-0 transition hover:border-brand-navy hover:text-brand-navy focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                      >
                        <PlusIcon className="h-3.5 w-3.5" /> Add
                      </button>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Coverage summary */}
        <div className="sticky left-0 z-10 border-t-2 border-slate-200 bg-slate-50 px-3 py-2.5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Coverage</div>
          <div className="text-[11px] text-slate-400">Who’s where · who’s free</div>
        </div>
        {days.map((day) => (
          <CoverageCell key={day} day={day} doctors={doctors} blocks={blocks} hospitalsById={hospitalsById} />
        ))}
      </div>
    </div>
  );
};

const CoverageCell = ({ day, doctors, blocks, hospitalsById }) => {
  const dayBlocks = blocks.filter((b) => b.bdate === day);
  const byId = Object.fromEntries(doctors.map((d) => [d.uid, d]));
  const names = (type) => [...new Set(dayBlocks.filter((b) => b.btype === type).map((b) => b.buid))].map((uid) => surname(byId[uid]));

  const hospitals = {};
  dayBlocks
    .filter((b) => b.btype === 'theatre')
    .forEach((b) => {
      const label = hospitalLabel(hospitalsById[b.bhid]) || 'No hospital';
      (hospitals[label] = hospitals[label] || new Set()).add(b.buid);
    });

  const onCall = names('on_call');
  const away = names('leave');
  const busy = new Set(dayBlocks.map((b) => b.buid));
  const free = isWeekend(day) ? [] : doctors.filter((d) => !busy.has(d.uid)).map(surname);
  const nothing = !Object.keys(hospitals).length && !onCall.length && !away.length && !free.length;

  return (
    <div className="flex flex-col gap-1 border-l border-t-2 border-slate-200 bg-slate-50 p-2 text-[11px] leading-4">
      {Object.entries(hospitals).map(([label, set]) => (
        <div key={label} className="flex items-center justify-between gap-2">
          <span className="truncate text-slate-600">{label}</span>
          <span className="rounded bg-brand-navy/10 px-1.5 font-bold text-brand-navy" title={`${set.size} doctor${set.size === 1 ? '' : 's'} in theatre`}>
            {set.size}
          </span>
        </div>
      ))}
      {onCall.length > 0 && (
        <div className="flex items-start gap-1 text-slate-600">
          <MoonIcon className="mt-0.5 h-3 w-3 shrink-0 text-indigo-500" />
          <span>{onCall.join(', ')}</span>
        </div>
      )}
      {away.length > 0 && <div className="text-slate-400">Away: {away.join(', ')}</div>}
      {free.length > 0 && <div className="font-semibold text-brand-mid">Free: {free.join(', ')}</div>}
      {nothing && <div className="text-slate-300">—</div>}
    </div>
  );
};

export default RosterView;
